import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

interface ToolbarPopoverProps {
  label: string
  icon: ReactNode
  disabled?: boolean
  /** A function receives `close`, for controls that should dismiss the panel once used. */
  children: ReactNode | ((close: () => void) => ReactNode)
}

/** A toolbar icon that opens its controls in a floating panel under it, like Google Sheets' align menu. */
export function ToolbarPopover({ label, icon, disabled = false, children }: ToolbarPopoverProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({ left: 8, top: 8 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  // A press inside a nested portal (a color picker's panel) is outside the panel's DOM but
  // still bubbles through its React tree. The document listener captures, because grid cells
  // stop their mousedown from bubbling, so it decides only after React has handled the press.
  const pressedInsideRef = useRef(false)

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => {
      const target = event.target as Node
      pressedInsideRef.current = false
      setTimeout(() => {
        if (pressedInsideRef.current) return
        if (!buttonRef.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false)
      })
    }
    const onKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', close, true)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', close, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  useLayoutEffect(() => {
    if (!open) return
    const update = () => {
      const rect = buttonRef.current?.getBoundingClientRect()
      if (!rect) return
      const width = panelRef.current?.offsetWidth ?? 0
      setPosition({
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8)),
        top: rect.bottom + 4,
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open])

  return <>
    <button
      ref={buttonRef}
      type="button"
      aria-label={label}
      title={label}
      aria-haspopup="dialog"
      aria-expanded={open}
      disabled={disabled}
      onClick={() => setOpen((prev) => !prev)}
      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors disabled:opacity-50 ${open ? 'bg-brand/5 text-brand' : 'hover:bg-surface-hover'}`}
    >
      {icon}
    </button>
    {open && createPortal(
      <div
        ref={panelRef}
        onMouseDown={() => { pressedInsideRef.current = true }}
        role="dialog"
        aria-label={label}
        style={{ position: 'fixed', left: position.left, top: position.top }}
        className="z-50 flex max-w-[calc(100vw-1rem)] flex-wrap items-center gap-2 rounded-xl border border-border bg-surface p-1.5 text-sm sm:text-xs shadow-xl"
      >
        {typeof children === 'function' ? children(() => setOpen(false)) : children}
      </div>,
      document.body,
    )}
  </>
}
